import {useEffect, useState} from 'react';

import {getRecipientFromThread} from 'shells/inbox/helpers';
import {ChannelViewLayout} from 'shells/inbox/messages/channel-view-layout';
import {ChatSidebarHeader} from 'shells/inbox/sidebar/chat-sidebar-header';
import type {Recipient} from 'shells/inbox/types';
import {Stack} from 'shells/layout/stack/stack';
import {Sidebar} from 'shells/sidebar/sidebar';

import {ThreadBotStatus, ThreadStatus} from 'nutshell-graphql-types';

import {OpenableModals, useOpenableModal} from '../../../openable-modals';

import {appHistory} from '../../../../history';

import {useGetChannelEvents, useGetInboxThreadsByIds} from '../../graphql/hooks';

import {InboxSidebarThreadList} from './inbox-sidebar-thread-list';
import {ThreadListSidebarHeader} from './thread-list-sidebar-header';
import {ThreadSummary} from './thread-summary';

import styles from './thread-list-sidebar-wrapper.module.css';

interface Props {
    entityId?: string;
    openedFromEntityName?: string;
    threadIds: string[];
    title: string;
    subtitle: string;
}

export function ThreadListSidebarWrapper(props: Props) {
    const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
    const [isViewingNewThread, setIsViewingNewThread] = useState(false);

    const {onClose} = useOpenableModal(OpenableModals.THREAD_LIST_SIDEBAR);
    const {threads, isLoading: isLoadingThreads} = useGetInboxThreadsByIds(
        props.threadIds,
        ThreadBotStatus.BOT_ENABLED
    );

    // Close modal when route changes
    useEffect(() => {
        const unlisten = appHistory.listen(() => {
            onClose();
        });

        return unlisten;
    }, [onClose]);

    const effectiveSelectedThreadId = selectedThreadId ?? (threads[0]?.id || null);
    const selectedThread =
        threads.find((thread) => thread.id === effectiveSelectedThreadId) ?? null;
    const selectedChannelId = selectedThread?.externalChannel?.id ?? null;

    const {
        channelEvents,
        isLoading: isLoadingChannelEvents,
        fetchMostRecentChannelEvents,
        refetchScheduledChannelEvents,
        hasNextPage,
        fetchMoreRows,
        lastSeenTime,
        isActive,
        sendabilityStatus,
    } = useGetChannelEvents(selectedChannelId, effectiveSelectedThreadId);

    useEffect(() => {
        if (isViewingNewThread && !isLoadingChannelEvents && channelEvents.length > 0) {
            setIsViewingNewThread(false);
        }
    }, [isViewingNewThread, isLoadingChannelEvents, channelEvents]);

    const handleSelectThread = (threadId: string) => {
        setIsViewingNewThread(true);
        setSelectedThreadId(threadId);
    };

    const selectedThreadStatus = selectedThread?.effectiveStatus;
    const shouldScrollToBottomOnInitialLoad = selectedThreadStatus !== ThreadStatus.CLOSED;

    const recipient: Recipient | null = selectedThread
        ? ({
              ...getRecipientFromThread(selectedThread),
              lastSeenTime: lastSeenTime || '',
              isActive,
          } as Recipient)
        : null;

    return (
        <Sidebar
            isOpen={true}
            preventBodyScroll={true}
            canClickOutsideToClose={true}
            canEscapeToClose={true}
            onClose={onClose}
            wide={true}
        >
            <Stack isFullHeight isFullWidth>
                <ThreadListSidebarHeader
                    title={props.title}
                    subtitle={props.subtitle}
                    onClose={onClose}
                />
                <div className='flex flex-dir-row min-height-0 full-height'>
                    <InboxSidebarThreadList
                        threads={threads}
                        numThreads={props.threadIds.length}
                        isLoading={isLoadingThreads}
                        selectedThreadId={effectiveSelectedThreadId}
                        onSelectThread={handleSelectThread}
                    />
                    <div className='flex flex-dir-col min-width-0 flex-grow'>
                        <ChatSidebarHeader
                            isLoading={
                                isLoadingThreads || (isLoadingChannelEvents && !channelEvents)
                            }
                            recipient={recipient}
                            status={selectedThreadStatus}
                            openedFromEntityId={props.entityId}
                            onUpdateStatus={() => Promise.resolve()}
                            shouldHidePickers={true}
                            assigneeButton={undefined}
                        />
                        {!isLoadingThreads && selectedThread?.threadSummary && (
                            <ThreadSummary summary={selectedThread.threadSummary} />
                        )}
                        <div className={styles['channel-view-wrapper']}>
                            <ChannelViewLayout
                                contentFillsContainer={true}
                                isLoadingChannelEvents={isLoadingChannelEvents}
                                isLoadingTextables={isLoadingThreads}
                                channelEvents={isViewingNewThread ? [] : channelEvents}
                                fetchMoreRows={fetchMoreRows}
                                hasNextPage={hasNextPage}
                                onCloseSidebar={onClose}
                                fetchMostRecentChannelEvents={fetchMostRecentChannelEvents}
                                refetchScheduledChannelEvents={refetchScheduledChannelEvents}
                                openedFromEntityName={props.openedFromEntityName}
                                cantSendStatus={sendabilityStatus}
                                shouldHideMessageInput={
                                    selectedThreadStatus === ThreadStatus.CLOSED
                                }
                                recipient={recipient}
                                threadId={effectiveSelectedThreadId || undefined}
                                threadType={selectedThread?.threadType}
                                shouldScrollToBottomOnInitialLoad={
                                    shouldScrollToBottomOnInitialLoad
                                }
                                isSidebar={true}
                            />
                        </div>
                    </div>
                </div>
            </Stack>
        </Sidebar>
    );
}
